import React, { useRef, useState } from "react"
import Button from "@mui/material/Button"
import FormControl from "@mui/material/FormControl"
import TextField from "@mui/material/TextField"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import Checkbox from "@mui/material/Checkbox"
import FormControlLabel from "@mui/material/FormControlLabel"
import Switch from "@mui/material/Switch"
import { useForm } from "react-hook-form"
import { useSnackbar } from "notistack"
import { verifyResponse } from "utils/httpRequest"
import { addNpsQuestion, editNpsQuestion } from "services/npsQuestion"
import { StyledNpsQuestionDialog } from "./NpsQuestions.style"

const TIER_OPTIONS = ["free", "silver", "gold", "platinum", "lifetime"]

const NpsQuestionDlg = (props) => {
  const { enqueueSnackbar } = useSnackbar()
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      questionText: "",
      versionLabel: "",
      minDaysSinceRegistration: 3,
    },
  })

  const initNpsQuestion = {
    id: 0,
    questionText: "",
    versionLabel: "",
    targetTiers: [],
    isActive: 0,
    minDaysSinceRegistration: 3,
  }

  const { onSubmit } = props
  const [isEditMode, setIsEditMode] = useState(false)
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [targetTiers, setTargetTiers] = useState([])
  const [isActive, setIsActive] = useState(false)

  const npsQuestionRef = useRef(initNpsQuestion)

  const openDlg = (editNpsQuestionItem) => {
    reset()
    if (editNpsQuestionItem) {
      npsQuestionRef.current = editNpsQuestionItem
      setValue("questionText", editNpsQuestionItem.questionText || "")
      setValue("versionLabel", editNpsQuestionItem.versionLabel || "")
      setValue(
        "minDaysSinceRegistration",
        editNpsQuestionItem.minDaysSinceRegistration ?? 3
      )
      setTargetTiers(editNpsQuestionItem.targetTiers || [])
      setIsActive(Boolean(editNpsQuestionItem.isActive))
      setIsEditMode(true)
    } else {
      npsQuestionRef.current = initNpsQuestion
      setValue("questionText", "")
      setValue("versionLabel", "")
      setValue("minDaysSinceRegistration", 3)
      setTargetTiers([])
      setIsActive(false)
      setIsEditMode(false)
    }
    setOpen(true)
  }

  const allTiersSelected = targetTiers.length === 0

  const toggleAllTiers = () => {
    setTargetTiers([])
  }

  const toggleTier = (tier) => {
    setTargetTiers((current) => {
      const base = current.length === 0 ? [] : current
      return base.includes(tier)
        ? base.filter((item) => item !== tier)
        : [...base, tier]
    })
  }

  const createNewNpsQuestion = async ({ questionText, versionLabel, minDaysSinceRegistration }) => {
    setLoading(true)
    try {
      const response = await addNpsQuestion({
        questionText,
        versionLabel,
        targetTiers,
        isActive: isActive ? 1 : 0,
        minDaysSinceRegistration,
      })
      verifyResponse(response)
      if (response.rtnCode === 200) {
        enqueueSnackbar("NPS question created", { variant: "success" })
        onSubmit && (await onSubmit())
        setOpen(false)
      } else {
        enqueueSnackbar("Failed to create NPS question", { variant: "warning" })
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message || "Failed to create NPS question",
        { variant: "warning" }
      )
    } finally {
      setLoading(false)
    }
  }

  const updateNpsQuestion = async ({ questionText, versionLabel, minDaysSinceRegistration, id }) => {
    setLoading(true)
    try {
      const response = await editNpsQuestion({
        id,
        questionText,
        versionLabel,
        targetTiers,
        isActive: isActive ? 1 : 0,
        minDaysSinceRegistration,
      })
      verifyResponse(response)
      if (response.rtnCode === 200) {
        enqueueSnackbar("NPS question updated", { variant: "success" })
        onSubmit && (await onSubmit())
        setOpen(false)
      } else {
        enqueueSnackbar("Failed to update NPS question", { variant: "warning" })
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message || "Failed to update NPS question",
        { variant: "warning" }
      )
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleAction = async (data) => {
    if (isEditMode) {
      await updateNpsQuestion({ ...data, id: npsQuestionRef.current.id })
      return
    }
    await createNewNpsQuestion(data)
  }

  const dlg = (
    <StyledNpsQuestionDialog open={open} onClose={handleClose}>
      <form onSubmit={handleSubmit(handleAction)}>
        <DialogTitle>
          <div className="dialog-title-group">
            <h2 className="dialog-title">
              {isEditMode ? "Edit NPS Question" : "Add NPS Question"}
            </h2>
            <p className="dialog-subtitle">
              Only one enabled question may target a given tier at a time.
            </p>
          </div>
        </DialogTitle>
        <DialogContent>
          <FormControl>
            <TextField
              autoFocus
              autoComplete="off"
              id="nps-question-text"
              label="Question text"
              type="text"
              fullWidth
              multiline
              rows={2}
              defaultValue={npsQuestionRef.current.questionText || ""}
              {...register("questionText", { required: true })}
              variant="outlined"
              size="small"
              error={!!errors?.questionText}
              helperText={!!errors?.questionText ? "* Question text is required" : " "}
            />
          </FormControl>

          <FormControl>
            <TextField
              autoComplete="off"
              id="nps-question-version"
              label="Version label"
              type="text"
              placeholder="e.g. V1"
              fullWidth
              defaultValue={npsQuestionRef.current.versionLabel || ""}
              {...register("versionLabel", { required: true })}
              variant="outlined"
              size="small"
              error={!!errors?.versionLabel}
              helperText={!!errors?.versionLabel ? "* Version label is required" : " "}
            />
          </FormControl>

          <FormControl>
            <TextField
              autoComplete="off"
              id="nps-question-min-days"
              label="Minimum days since registration"
              type="number"
              fullWidth
              defaultValue={npsQuestionRef.current.minDaysSinceRegistration ?? 3}
              {...register("minDaysSinceRegistration", {
                required: true,
                valueAsNumber: true,
                min: 0,
              })}
              variant="outlined"
              size="small"
              inputProps={{ min: 0, step: 1 }}
              error={!!errors?.minDaysSinceRegistration}
              helperText={
                !!errors?.minDaysSinceRegistration
                  ? "* Must be 0 or greater"
                  : "Only show this question to members registered at least this many days ago"
              }
            />
          </FormControl>

          <div className="label-text">Target tiers</div>
          <div className="tier-checkbox-group">
            <FormControlLabel
              control={
                <Checkbox
                  checked={allTiersSelected}
                  onChange={toggleAllTiers}
                  size="small"
                />
              }
              label="All tiers"
            />
            {TIER_OPTIONS.map((tier) => (
              <FormControlLabel
                key={tier}
                control={
                  <Checkbox
                    checked={!allTiersSelected && targetTiers.includes(tier)}
                    onChange={() => toggleTier(tier)}
                    size="small"
                  />
                }
                label={tier.charAt(0).toUpperCase() + tier.slice(1)}
              />
            ))}
          </div>

          <div className="active-toggle-row">
            <Switch
              checked={isActive}
              onChange={(event) => setIsActive(event.target.checked)}
            />
            <span>{isActive ? "Enabled (live)" : "Disabled"}</span>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            className="dialog-cancel-btn"
            variant="outlined"
            size="medium"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            className="dialog-submit-btn"
            type="submit"
            variant="contained"
            size="medium"
            disabled={loading}
          >
            {isEditMode ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </StyledNpsQuestionDialog>
  )

  return [openDlg, dlg, loading]
}

export default NpsQuestionDlg
